import { type CopyRead, type RecoveryCopy, type RecoveryEnvironment, type RecoverySource, encodeRecoveryCopy, readRecoveryCopy, recoverySummary, sameRecoverySource } from './recovery-copy';

export interface NativeStringStore { getItem(key: string): Promise<string | null>; setItem(key: string, value: string): Promise<void>; removeItem(key: string): Promise<void> }
export type RecoveryView = { status: 'checking' | 'absent' | 'verified' | 'review' | 'failed' | 'busy'; message: string; source: RecoverySource | null; current: CopyRead; previous: CopyRead };
const missing: CopyRead = { kind: 'missing', raw: null };
export type RecoveryDependencies = { storage: NativeStringStore; environment: RecoveryEnvironment; readSource(): Promise<RecoverySource>; restore(source: RecoverySource, expected: RecoverySource): Promise<void>; deleteSource(): Promise<void> };

/** A queue coordinates this instance only. Native Storage has no cross-WebView CAS. */
export class RecoveryController {
  view: RecoveryView = { status: 'checking', message: '기기의 원본과 복구 사본을 확인하고 있어요.', source: null, current: missing, previous: missing };
  readonly keys: readonly [string, string];
  private listeners = new Set<() => void>();
  private queue: Promise<unknown> = Promise.resolve();
  private readyForAutomaticCopy = false;
  private generation = 0;
  private automaticQueued = false;
  private restoring = false;
  constructor(private readonly deps: RecoveryDependencies) { this.keys = [`carryonpan.recovery.v1.${deps.environment}.current`, `carryonpan.recovery.v1.${deps.environment}.previous`]; }
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  private show(patch: Partial<RecoveryView>) { this.view = { ...this.view, ...patch }; for (const listener of this.listeners) listener(); }
  private run(operation: () => Promise<void>) {
    const next = this.queue.then(operation, operation).catch((error) => {
      this.readyForAutomaticCopy = false;
      this.show({ status: 'failed', message: error instanceof Error ? error.message : '복구 사본을 확인하지 못했어요. 기기의 원본은 자동으로 바뀌지 않습니다.' });
    });
    this.queue = next;
    return next;
  }
  private async readCopies() {
    let current: string | null; let previous: string | null;
    try { [current, previous] = await Promise.all(this.keys.map((key) => this.deps.storage.getItem(key))) as [string | null, string | null]; }
    catch { throw new Error('복구 사본 저장소에 연결하지 못했어요. 기기의 여행은 그대로이며 다시 확인하거나 파일로 보관할 수 있어요.'); }
    return { current: await readRecoveryCopy(current, this.deps.environment), previous: await readRecoveryCopy(previous, this.deps.environment) };
  }
  private async inspectNow() {
    const copies = await this.readCopies();
    this.readyForAutomaticCopy = false;
    this.show({ ...copies, source: null, status: 'checking' });
    let source: RecoverySource;
    try { source = await this.deps.readSource(); }
    catch { throw new Error('기기의 여행 원본을 읽지 못했어요. 아래 남아 있는 사본은 파일로 받을 수 있습니다. 내 여행의 원본 복구 안내도 확인해주세요.'); }
    const current = copies.current;
    this.readyForAutomaticCopy = current.kind === 'valid' && sameRecoverySource(source, current.copy.source);
    const review = current.kind !== 'missing' || copies.previous.kind !== 'missing';
    this.show({ ...copies, source, status: this.readyForAutomaticCopy ? 'verified' : review ? 'review' : 'absent', message: this.readyForAutomaticCopy ? '현재 여행과 복구 사본의 내용이 일치해요.' : review ? '기기의 현재 여행과 별도 사본을 확인해주세요. 자동으로 덮거나 복원하지 않았어요.' : '아직 기기 복구 사본이 없어요. 현재 여행의 사본을 만들 수 있어요.' });
  }
  inspect = () => this.run(() => this.inspectNow());
  sourceChanged = () => {
    if (this.restoring) return Promise.resolve();
    if (this.automaticQueued) return this.queue;
    this.automaticQueued = true;
    const generation = this.generation;
    return this.run(async () => {
      this.automaticQueued = false;
      if (generation !== this.generation) return;
      if (!this.readyForAutomaticCopy && this.view.status !== 'absent') return;
      const expected = this.view.current.raw;
      await this.writeCopy(expected);
    });
  };
  private async writeCopy(expected: string | null) {
    const source = await this.deps.readSource();
    const copies = await this.readCopies();
    if (copies.current.raw !== expected) { await this.inspectNow(); throw new Error('다른 화면에서 복구 사본이 바뀌었어요. 두 내용을 다시 확인해주세요.'); }
    if (copies.current.kind === 'corrupt' || copies.previous.kind === 'corrupt') { this.show({ ...copies, source }); throw new Error('읽지 못한 복구 사본을 먼저 파일로 보관하거나 확인 후 지워주세요. 자동 덮어쓰기는 멈췄어요.'); }
    if (copies.current.kind === 'valid' && sameRecoverySource(source, copies.current.copy.source)) { await this.inspectNow(); return; }
    const copy = await encodeRecoveryCopy(source, this.deps.environment);
    this.show({ status: 'busy', message: '기기에 복구 사본을 쓰고 다시 읽어 확인하고 있어요.' });
    try {
      // Preserve the last readable copy before replacing current. Each write has exact read-back.
      if (copies.current.kind === 'valid') {
        await this.deps.storage.setItem(this.keys[1], copies.current.raw);
        if (await this.deps.storage.getItem(this.keys[1]) !== copies.current.raw) throw new Error();
      }
      if (await this.deps.storage.getItem(this.keys[0]) !== expected) throw new Error();
      await this.deps.storage.setItem(this.keys[0], copy.raw);
      if (await this.deps.storage.getItem(this.keys[0]) !== copy.raw) throw new Error();
    } catch {
      // Never roll back a head: another WebView may have changed it after our read.
      this.show({ ...(await this.readCopies()), source });
      throw new Error('복구 사본 저장을 확인하지 못했어요. 여행 원본은 그대로예요. 이전 사본 확인·재시도·여행 파일 받기를 이용해주세요.');
    }
    const latest = await this.deps.readSource();
    const stillCurrent = sameRecoverySource(source, latest);
    this.readyForAutomaticCopy = true;
    this.show({ current: { kind: 'valid', raw: copy.raw, copy }, previous: copies.current.kind === 'valid' ? copies.current : copies.previous, source: latest, status: stillCurrent ? 'verified' : 'checking', message: stillCurrent ? `기기 복구 사본을 확인했어요. ${recoverySummary(source)}` : '기기에 더 최근 변경이 있어요. 최신 내용의 사본을 다시 확인하고 있습니다.' });
    if (!stillCurrent) void this.sourceChanged();
  }
  backup = (expectedCurrent: string | null) => this.run(() => this.writeCopy(expectedCurrent));
  restore = (copy: RecoveryCopy, expected: RecoverySource, tripIds: readonly string[], includeBagProfiles: boolean) => this.run(async () => {
    this.readyForAutomaticCopy = false;
    this.generation++;
    const copies = await this.readCopies();
    if (![copies.current, copies.previous].some((entry) => entry.kind === 'valid' && entry.raw === copy.raw)) throw new Error('선택한 사본이 바뀌었어요. 다시 확인해주세요.');
    const selected = { trips: copy.source.trips.filter((state) => tripIds.includes(state.trip.id)), bagProfiles: includeBagProfiles ? copy.source.bagProfiles : [] };
    if (!selected.trips.length && !selected.bagProfiles.length) throw new Error('복구할 여행이나 내 가방을 선택해주세요.');
    this.show({ status: 'busy', message: '선택한 자료와 현재 원본을 다시 비교하고 있어요.' });
    this.restoring = true;
    try { await this.deps.restore(selected, expected); } finally { this.restoring = false; }
    await this.inspectNow();
    this.show({ message: '선택한 자료를 복원하고 다시 읽어 확인했어요. 선택하지 않은 현재 여행은 그대로예요.' });
  });
  removeCopy = (slot: 0 | 1, expected: string) => this.run(async () => {
    this.readyForAutomaticCopy = false; this.generation++;
    if (await this.deps.storage.getItem(this.keys[slot]) !== expected) throw new Error('사본이 바뀌어 삭제하지 않았어요. 다시 확인해주세요.');
    await this.deps.storage.removeItem(this.keys[slot]);
    if (await this.deps.storage.getItem(this.keys[slot]) !== null) throw new Error('복구 사본 삭제를 확인하지 못했어요.');
    await this.inspectNow();
  });
  deleteAll = () => {
    this.readyForAutomaticCopy = false; this.generation++;
    return this.run(async () => {
      this.show({ status: 'busy', message: '이 환경의 복구 사본과 기기 원본을 삭제하고 확인하고 있어요.' });
      try {
        for (const key of this.keys) { await this.deps.storage.removeItem(key); if (await this.deps.storage.getItem(key) !== null) throw new Error(); }
      } catch { throw new Error('복구 사본을 모두 지웠는지 확인하지 못했어요. 여행 원본은 지우지 않았습니다. 다시 삭제를 시도해주세요.'); }
      await this.deps.deleteSource();
      await this.inspectNow();
      this.show({ message: '이 환경의 여행 원본과 복구 사본을 지우고 다시 확인했어요. 다른 기기·공유받은 파일·구매 권리는 별도예요.' });
    });
  };
}
